import "./SlideThumbnailWidget.scss";

import classNames from "classnames";
import { useHotkeys } from "react-hotkeys-hook";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { CaseSlide } from "pwapi";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectSortedCaseDetail from "shared/selectors/selectSortedCaseDetail";
import { SortedSlideBase } from "shared/utils/slideUtils";

import useViewerItems, { ViewerItem } from "hooks/useViewerItems";
import { useAppSelector } from "store/hooks";

import { getMostRecentScreenRequest } from "./dialogs/utils";
import Thumbnail from "./Thumbnail";

interface SlideThumbnailWidgetProps {
  highlightedSlideId?: number;
  highlightedDocumentId?: number;
  selectedSpecimenId?: number;
  canFlag?: boolean;
}

type SortedCaseSlide = Omit<CaseSlide, "stain"> & SortedSlideBase;

const SlideThumbnailWidget = (props: SlideThumbnailWidgetProps) => {
  const {
    highlightedSlideId,
    highlightedDocumentId,
    selectedSpecimenId,
    canFlag,
  } = props;
  const navigate = useNavigate();
  const viewerBarcodeScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "viewerBarcodeScanning")
  );
  const sortedCase = useAppSelector(selectSortedCaseDetail);
  const { id: caseId, slides } = sortedCase;

  const highlightedSlide = sortedCase.slides.find(
    (s) => s.id === highlightedSlideId
  );

  const highlightedSpecimenId =
    selectedSpecimenId ||
    highlightedSlide?.specimen_id ||
    sortedCase.specimens?.find((specimen) =>
      specimen.case_documents?.some(
        (document) => document.id === highlightedDocumentId
      )
    )?.id;

  const availableSpecimens = sortedCase.specimens.filter(
    (specimen) =>
      specimen.case_documents?.length ||
      sortedCase.slides.some(
        (slide) =>
          slide.status === "success" && slide.specimen_id === specimen.id
      )
  );

  const highlightedSpecimenIndex = availableSpecimens.findIndex(
    (specimen) => specimen.id === highlightedSpecimenId
  );

  const hightLightedSpecimenSlides = sortedCase.slides.filter(
    (slide) => slide?.specimen_id === highlightedSpecimenId
  );

  const viewerItems = useViewerItems();
  const highlightedIndex = viewerItems.findIndex((v) =>
    highlightedSlideId
      ? v.id === highlightedSlideId && v.type === "slides"
      : highlightedDocumentId
      ? v.id === highlightedDocumentId && v.type === "documents"
      : false
  );

  const viewerItemsBySpecimenId: { [key: number]: ViewerItem[] } = {};
  const sortedSpecimenIds: Array<number | undefined> = [];
  viewerItems.forEach((item) => {
    if (!viewerItemsBySpecimenId[item.specimenId || 0])
      viewerItemsBySpecimenId[item.specimenId || 0] = [];
    viewerItemsBySpecimenId[item.specimenId || 0].push(item);
    if (!sortedSpecimenIds.includes(item.specimenId))
      sortedSpecimenIds.push(item.specimenId);
  });

  useHotkeys(
    "1,2,3,4,5,6,7,8,9",
    (key) => {
      const index = parseInt(key.key);
      if (typeof hightLightedSpecimenSlides[index - 1] !== "undefined") {
        navigate(
          `/cases/${caseId}/slides/${hightLightedSpecimenSlides[index - 1].id}`
        );
      }
    },
    { scopes: ["viewerHotkeys"] },
    [hightLightedSpecimenSlides, caseId]
  );
  useHotkeys(
    ",!f",
    () => {
      if (!highlightedIndex) return;
      const prevItem = viewerItems[highlightedIndex - 1];

      navigate(`/cases/${caseId}/${prevItem.type}/${prevItem.id}`);
    },
    { splitKey: "!", scopes: ["viewerHotkeys"] },
    [caseId, viewerItems, highlightedIndex]
  );
  useHotkeys(
    ".,g",
    () => {
      if (
        highlightedIndex === undefined ||
        highlightedIndex === -1 ||
        highlightedIndex === viewerItems.length - 1
      )
        return;
      const nextItem = viewerItems[highlightedIndex + 1];

      navigate(`/cases/${caseId}/${nextItem.type}/${nextItem.id}`);
    },
    { scopes: ["viewerHotkeys"] },
    [caseId, viewerItems, highlightedIndex]
  );

  const handlePrevSpecimen = (startAtEnd = false) => {
    if (highlightedSpecimenIndex > -1 && highlightedSpecimenIndex !== 0) {
      const prevSpecimen = availableSpecimens[highlightedSpecimenIndex - 1];
      const prevSpecimenSlides = slides.filter(
        (slide) =>
          slide.status === "success" && slide.specimen_id === prevSpecimen.id
      );
      const prevSpecimenDocuments = prevSpecimen.case_documents || [];
      if (prevSpecimenDocuments.length) {
        navigate(
          `/cases/${caseId}/documents/${
            prevSpecimenDocuments[
              startAtEnd ? prevSpecimenDocuments.length - 1 : 0
            ].id
          }`
        );
      } else if (prevSpecimenSlides.length) {
        navigate(
          `/cases/${caseId}/slides/${
            prevSpecimenSlides[startAtEnd ? prevSpecimenSlides.length - 1 : 0]
              .id
          }`
        );
      }
    }
  };
  useHotkeys(
    "{",
    () => handlePrevSpecimen(false),
    { ignoreModifiers: true, scopes: ["viewerHotkeys"] },
    [
      handlePrevSpecimen,
      availableSpecimens,
      highlightedSpecimenIndex,
      highlightedDocumentId,
      caseId,
    ]
  );

  const handleNextSpecimen = () => {
    if (
      highlightedSpecimenIndex > -1 &&
      highlightedSpecimenIndex < availableSpecimens.length - 1
    ) {
      const nextSpecimen = availableSpecimens[highlightedSpecimenIndex + 1];
      const nextSpecimenSlides = slides.filter(
        (slide) =>
          slide.status === "success" && slide.specimen_id === nextSpecimen.id
      );
      const nextSpecimenDocuments = nextSpecimen.case_documents || [];
      if (nextSpecimenSlides.length) {
        navigate(`/cases/${caseId}/slides/${nextSpecimenSlides[0].id}`);
      } else if (nextSpecimenDocuments.length) {
        navigate(`/cases/${caseId}/documents/${nextSpecimenDocuments[0].id}`);
      }
    }
  };

  const mostRecentScreenRequest = getMostRecentScreenRequest(
    sortedCase.screen_requests
  );

  useHotkeys(
    "}",
    () => handleNextSpecimen(),
    { ignoreModifiers: true, scopes: ["viewerHotkeys"] },
    [
      handleNextSpecimen,
      availableSpecimens,
      highlightedSpecimenIndex,
      highlightedDocumentId,
      caseId,
    ]
  );

  return (
    <div className="Slides">
      {sortedSpecimenIds.map((specimenId, _index) => {
        const specimenViewerItems =
          viewerItemsBySpecimenId[specimenId || 0] || [];
        const specimen = sortedCase.specimens?.find((s) => s.id === specimenId);
        if (highlightedSpecimenId === specimenId) {
          return (
            <div
              key={`${specimenId}`}
              className={classNames(
                "Slides__specimen Slides__specimen--selected",
                {
                  "Slides__specimen--empty":
                    specimenViewerItems.length === 0 ||
                    (specimenViewerItems.length === 1 &&
                      specimenViewerItems[0].type === "specimens"),
                }
              )}
            >
              <strong>{specimen?.name || ""}</strong>
              {specimenViewerItems.map((viewerItem, innerIndex) => {
                if (viewerItem.type === "slides") {
                  const slide = sortedCase.slides.find(
                    (s) => s.id === viewerItem.id
                  );
                  if (!slide) return null;
                  return (
                    <Link
                      key={`${caseId}_${slide.id}`}
                      to={`/cases/${caseId}/slides/${slide.id}`}
                    >
                      <Thumbnail
                        key={`slide_${innerIndex}`}
                        specimen={specimen?.name}
                        highlighted={slide.id === highlightedSlideId}
                        viewed={
                          sortedCase.is_screening
                            ? slide.specimen_id &&
                              (mostRecentScreenRequest?.specimens?.includes(
                                slide.specimen_id
                              ) ||
                                !sortedCase.screen_requests.length)
                              ? (slide as SortedCaseSlide).was_previously_viewed
                              : true
                            : (slide as SortedCaseSlide).was_previously_viewed
                        }
                        diagnostic={false}
                        flagged={
                          canFlag &&
                          (slide as SortedCaseSlide).flags &&
                          (slide as SortedCaseSlide).flags.length > 0
                        }
                        archived={false}
                        rotation={
                          (slide as SortedCaseSlide).slide_rotation_degrees
                        }
                        low_power_tile_url={
                          (slide as SortedCaseSlide).low_power_tile_url
                        }
                        slide={slide as SortedCaseSlide}
                      />
                    </Link>
                  );
                } else if (viewerItem.type === "documents") {
                  const document = sortedCase.documents.find(
                    (d) => d.id === viewerItem.id
                  );
                  if (!document) return null;
                  return (
                    <Link
                      key={`${caseId}_${document.id}`}
                      to={`/cases/${caseId}/documents/${document.id}`}
                    >
                      {document.category}
                    </Link>
                  );
                } else {
                  return (
                    <>
                      <br />
                      <span>0 Slides</span>
                    </>
                  );
                }
              })}
            </div>
          );
        } else {
          const specimenSlideIds = specimenViewerItems
            .filter((item) => item.type === "slides")
            .map((item) => item.id);
          const specimenSlides = sortedCase.slides.filter((slide) =>
            specimenSlideIds.includes(slide.id || 0)
          );
          const hasBubble = specimenSlides.some((slide) =>
            sortedCase.is_screening
              ? !(slide as SortedCaseSlide).was_previously_viewed &&
                !!slide.specimen_id &&
                (mostRecentScreenRequest?.specimens?.includes(
                  slide.specimen_id
                ) ||
                  !sortedCase.screen_requests.length)
              : !(slide as SortedCaseSlide).was_previously_viewed
          );

          if (!specimenViewerItems.length) return null;

          return (
            <div
              className="Slides__specimen Slides__specimen--single"
              key={`${specimenId}`}
            >
              <Link
                to={`/cases/${caseId}/${specimenViewerItems.at(0)?.type}/${
                  specimenViewerItems.at(0)?.id
                }`}
              >
                {specimen?.name}
                <br />
                <span>
                  {hasBubble && <>&nbsp;&nbsp;&nbsp;&nbsp;</>}
                  {specimenSlides.length}
                  &nbsp;Slide{specimenSlides.length === 1 ? "" : "s"}
                </span>
              </Link>
              {hasBubble && (
                <div className="bubbles">
                  <span className="bubble bubble__not__viewed" />
                </div>
              )}
            </div>
          );
        }
      })}
    </div>
  );
};

export default SlideThumbnailWidget;
